import { useEffect, useState } from "react";
import { personalDetailsSteps } from "src/constants"
import type { IPersonalDetailsStep, IPersonalDetailsSteps, IUserDetails } from "src/types";
import {
  Box, Button, Select, TextInput,
} from "@mantine/core";
import { OnboardingProgress } from "src/components/onboardingProgress";
import { getProgressStepper, convertString } from "src/utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { ScreenWrapper } from "../../components"
import useStyles from "./personalDetails.styles";
import { uploadPersonalDetails } from "./redux/modificationSlice";
import type { IPersonalDetailsPayload } from "./redux/types";

interface PersonalDetailsEditProps {
  handleMoveToDocumentsScreen: () => void;
}

const parentsName = [
  {
    id: 'fatherName',
    heading: 'Father\'s name',
    description: 'Important for completing your KYC.',
    options: [],
    isEditable: true,
    inputType: 'text',
  },
  {
    id: 'motherName',
    heading: 'Mother\'s name',
    description: 'Important for completing your KYC.',
    options: [],
    isEditable: true,
    inputType: 'text',
  },
]

const PersonalDetailsEdit = ({ handleMoveToDocumentsScreen }:PersonalDetailsEditProps) => {
  const requiredFields: IPersonalDetailsSteps = personalDetailsSteps.filter((step) => step.id !== "segmentSelection")
  const userDetails : IUserDetails = useSelector((state: RootState) => state.account.userDetails);
  const [formValues, setFormValues] = useState<IPersonalDetailsPayload>({
    gender: userDetails.gender || '',
    maritalStatus: userDetails.maritalStatus || '',
    occupation: userDetails.occupation || '',
    incomeSegment: userDetails.incomeSegment || '',
    tradingExperience: userDetails.tradingExperience || '',
    fatherName: userDetails.fatherName || '',
    motherName: userDetails.motherName || '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data, loading, isError, status,
  } = useSelector((state: RootState) => state.modification.uploadPersonalDetails);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | string, name?: string) => {
    let value: string;
    let inputName: string;

    if (typeof e === 'string') {
    // This is for the Select component
      value = e;
      inputName = name || '';
    } else {
    // This is for the TextInput component
      value = e.target.value;
      inputName = e.target.name;
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [inputName]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [inputName]: "",
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Validation
    let isValid = true;
    const errors: { [key: string]: string } = {};
    requiredFields?.forEach((step: IPersonalDetailsStep) => {
      if (!formValues[step.id]) {
        isValid = false;
        errors[step.id] = `${convertString(step.id)} is required`;
      }
    });
    setFormErrors(errors);
    if (isValid) {
      dispatch(uploadPersonalDetails(formValues as IPersonalDetailsPayload));
    }
  };

  // if personal details are uploaded successfully, move to the document screen
  useEffect(() => {
    if (status === 200) {
      handleMoveToDocumentsScreen();
    }
  }, [data, isError, status]);

  return (
    <ScreenWrapper>
      <Box className="csMainContainer">
        <Box className="csHeader">
          <Box className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(userDetails.currentStep)} />
          </Box>
        </Box>
        <Box className={classes.contentContainer}>
          <h2 className="csHeading">
            Personal Details
          </h2>
          <p className="csDescription">
            Following details are required.
            {' '}
          </p>
          <form className={classes.formWrapper} onSubmit={handleSubmit}>
            {
              formValues && requiredFields?.map((step: IPersonalDetailsStep) => {
                if (step.id === "segmentSelection") return null;
                if (step.inputType === 'text') {
                  return parentsName.map((parent) => (
                    <TextInput
                      key={parent.id}
                      label={parent.heading}
                      placeholder={parent.description}
                      required
                      radius="md"
                      className="text_input"
                      name={parent.id}
                      value={formValues[parent.id] as string || ""}
                      onChange={handleInputChange}
                      error={formErrors[parent.id]}
                    />
                  ))
                }
                if (step.inputType === 'select') {
                  return (
                    <Select
                      key={step.id}
                      label={step.heading}
                      placeholder={step.description}
                      required
                      radius="md"
                      className="text_input"
                      data={step.options}
                      name={step.id}
                      value={formValues[step.id] || ""}
                      onChange={(value) => handleInputChange(value as string, step.id)}
                      error={formErrors[step.id]}
                    />
                  )
                }
                return null;
              })
            }
            <Button
              // disabled={buttonDisabled}
              className="heading_2xs_semibold text_base_white"
              type="submit"
              radius="md"
              // onClick={handleSubmitCall}
              loading={loading}
            >
              Submit
            </Button>
          </form>
        </Box>
      </Box>
    </ScreenWrapper>
  )
}

export default PersonalDetailsEdit
